import React, { useState } from "react";
import {momentOrNull, safeGet, YesNo} from "../../../common/util";
import {Button, Icon, message} from "antd";
import PaxDetail from "./Pax";
import CgoDetail from "./Cgo";
import { Cell, Column, SelectionModes, Table } from "@blueprintjs/table";
import SortableColumnHeaderCell from "components/blueprinttable/SortableColumnHeaderCell";
import useSortableData from "hooks/useSortableData";
import {Query} from "react-apollo";
import useManifestPrintout from "../../scheduling/Printout/useManifestPrintout";
import gql from "graphql-tag";
import WithOrgData from "../../WithOrgData";
import withCognitoUser from "../../WithCognitoUser";

const CarriableCount = ({ count, onClicked }) => {
  const text = `${count} item(s)`;
  if (!count) {
    return text;
  }
  return (
    <Button className="mc-link-btn" onClick={onClicked}>
      {text}
    </Button>
  );
};

const ManifestDetail = (props) => {
    const [selFlight, setSelFlight] = useState(null);
    const [carriableType, setCarriableType] = useState('pax');

    const mappedData = props.data.map((row) => {
        const getVal = (obj, field) => obj && obj[field];
        const legs = (row.legs && Object.values(row.legs)) || [];
        const haslegs = legs.length ? true : false;
        const firstleg = haslegs ? legs[0] : null;
        const departure = (firstleg && firstleg.departure) || (row.departure && row.departure.name) || null;
        const destinations = (haslegs && legs.map((leg) => leg.destination).join(', ')) || null;
        return {
            ...row,
            departure,
            destinations,
            tailNum: getVal(row.aircraft, 'tailNum'),
            customerName: getVal(row.customer, 'name'),
            transporterName: getVal(row.transporter, 'name'),
            finalized: row.nodeState === 'FINALIZED',
        };
    });

    const [ data, { setSortKey, currentSortKey, currentSortDir } ] = useSortableData(mappedData);

    const setSelectedFlight = (selFlight, carriableType = carriableType) => {
        setSelFlight(selFlight);
        setCarriableType(carriableType);
    };

    const getSelectedFlight = () => {
        if (!selFlight || !data) return undefined;
        return data.find((flight) => flight._id === selFlight);
    };

    const columns = [
        {
            name: 'Flight Designation',
            key: 'desig',
        },
        {
            name: 'Departure',
            key: 'departure',
        },
        {
            name: 'Destinations',
            key: 'destinations',
        },
        {
            name: 'Transporter',
            key: 'transporterName',
        },
        {
            name: 'Customer',
            key: 'customerName',
        },
        {
            name: 'Aircraft Tail Num',
            key: 'tailNum',
        },
        {
            name: 'Scheduled Flight Date',
            key: 'scheduledFlightDate',
            formatter: (value) => momentOrNull(value)?.format('YYYY-MM-DD'),
        },
        {
            name: 'Finalized',
            key: 'state',
            formatter: (value) => YesNo(value === 'FINALIZED' || value === 'RETIRED'),
        },
        {
            name: 'PAX Count',
            key: 'paxList',
            formatter: (value, row) => (
                <CarriableCount
                    count={value.length}
                    onClicked={() => setSelectedFlight(row._id, 'pax' )}
                />
            ),
        },
        {
            name: 'CGO Count',
            key: 'cgoList',
            formatter: (value, row) => (
                <CarriableCount
                    count={value.length}
                    onClicked={() => setSelectedFlight(row._id, 'cgo' )}
                />
            ),
        },
        {
            name: 'Export',
            Key: 'export',
            formatter: (_, row) => (
                renderExportButtons(row)
            )
        }
    ]
        .map((col) => {
            const cellRenderer = (i) => {
                const value = col.formatter ?
                    col.formatter(data[i]?.[col.key], data[i]) :
                    data[i]?.[col.key]
                return <Cell tooltip={value} style={{ display: 'flex', alignItems: 'center' }}>{value}</Cell>
            }
            return (
                <Column
                    key={col.key}
                    name={col.name}
                    cellRenderer={cellRenderer}
                    columnHeaderCellRenderer={
                        () => (
                            <SortableColumnHeaderCell
                                name={col.name}
                                sortKey={col.key}
                                onSort={setSortKey}
                                currentSortedKey={currentSortKey}
                                currentSortDir={currentSortDir}
                            />
                        )
                    }
                />
            )
        })

    const [ rowHeights, setRowHeights ] = useState(new Array(data?.length).fill(30));

    const renderFlightsGrid = () => {
        if (!data) return null;
        return (
            <Table
                selectionModes={SelectionModes.NONE}
                className="mc-table"
                numRows={data?.length}
                rowHeights={rowHeights}
                onRowHeightChanged={(i, size) => (
                    setRowHeights(curr => [...curr.slice(0, i), size, ...curr.slice(i+1, rowHeights.length)])
                )}
            >
                {columns}
            </Table>
        );
    };

    const renderExportButtons = (row) => {
        const FLIGHT = gql`
            query Flight($_id: ID!){
                getFlightHistory(_id: $_id){
                    _id
                    desig
                    scheduledFlightDate
                    callSign
                    customerID {
                        _id
                        name
                    }
                    paxIDList {
                        _id
                        lastName
                        firstName
                        scheduledOrder
                        scheduledGroup
                        employerID
                        employerDoc {
                            _id
                            name
                        }
                        transitType
                        departureID
                        departureDoc {
                            _id
                            name
                        }
                        destinationID
                        destinationDoc{
                            _id
                            name
                        }
                        paxWeight
                        bagWeight
                        bagCount
                    }
                    legs
                    legsArray {
                        departure
                        destination
                        departureID
                        destinationID
                        flightTime
                        paxIDs {
                            _id
                            lastName
                            firstName
                            employerID
                            employerDoc {
                                _id
                                name
                            }
                            chargeCode
                            paxWeight
                            bagWeight
                            departureID
                            departureDoc {
                                _id
                                name
                            }
                            destinationID
                            destinationDoc{
                                _id
                                name
                            }
                            personID
                            personDoc {
                                _id
                                dob
                                nation
                            }
                        }
                        cgoIDs{
                            _id
                            name
                            approvedBy
                            weight
                            assigned
                            checkInType
                            nodeState
                            hazmat
                            transitType
                            attentionTo
                            deliveredBy
                            notes
                            chargeCode
                            departureID
                            departureDoc{
                                _id
                                name
                            }
                            destinationID
                            destinationDoc{
                                _id
                                name
                            }
                        }
                    }
                    tpID {
                        _id
                    }
                    pilot {
                        _id
                        name {
                            lastName
                            firstName
                        }
                    }
                    copilot {
                        _id
                        name {
                            lastName
                            firstName
                        }
                    }
                    contract {
                        _id
                        name
                    }
                    aircraftID {
                        _id
                        tailNum
                    }
                    departureID {
                        _id
                        name
                    }
                }
            }
        `
        return (
            <Query
                query={FLIGHT}
                variables={{_id: row._id}}
                fetchPolicy="cache-and-network">
                {(flight) => {
                    var flightData = {};
                    if (flight && flight.data && flight.data.getFlightHistory){
                        flightData = flight.data.getFlightHistory || {};
                        flightData.departureDoc = {};
                        flightData.departureDoc["_id"] = flightData.legsArray[0].departureID;
                        flightData.departureDoc["name"] = flightData.legsArray[0].departure;
                    }
                    const { loading, error } = flight;
                    //console.log(column_sets[0].columns);
                    return <PrintoutButton flight={flightData} flightDesigText={flightData.desig} disabled={loading || error || !flight}  />
                }}
            </Query>
        );
    };

    const renderChildGrids = () => {
        switch (carriableType) {
            case 'pax':
                return <PaxDetail data={getSelectedFlight().paxList} minHeight="40rem" />;
            case 'cgo':
                return <CgoDetail data={getSelectedFlight().cgoList} minHeight="40rem" />;
            default:
                break;
        }
    };

    const selectedFlight = getSelectedFlight();

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {selectedFlight ? (
                <div className="mc-border-bottom" style={{ padding: 12, display: 'flex', alignItems: 'center' }}>
                    <Button icon="left" onClick={() => setSelFlight(null)} />
                    <h2 style={{ marginLeft: '1rem', marginBottom: 0, display: 'inline' }}>
                        {`${carriableType === 'pax' ? 'Passengers' : 'Cargo'} of flight ${selectedFlight.desig}`}
                    </h2>
                </div>
            ) : null}
            <div style={{ flex: 1, minHeight: 0 }}>
                {selectedFlight ? renderChildGrids() : renderFlightsGrid()}
            </div>
        </div>
    );
};

var PrintoutButton = ({flight, flightDesigText, orgData, cognitoUser, ...restProps}) => {
    const [ fetchPrintout, fetching ] = useManifestPrintout('FlightWithLegs', 'Manifest');
    const userAttr = cognitoUser.attributes;
    return (
        <Query
            query={gql`
                query GetCustomerManifestPrintout($id: ID!){
                    getCustomer(_id: $id){
                        _id
                        name
                        manifestPrintout {
                            type
                            layouts {
                                name
                                logo
                            }
                        }
                    }
                }
            `}
            variables={{ id: flight.customerID?._id }}
        >
            {({ data }) => {
                const manifestPrintouts = safeGet(['getCustomer', 'manifestPrintout'], data) || [];
                const flightManifest = manifestPrintouts.find((m) => m.type === 'Flight');
                const hessLayout = flightManifest && flightManifest.layouts.find((l) => l.name === 'Hess');
                const logoURL = hessLayout && hessLayout.logo;
                const handleClick = () => {
                    if (!flight || !flightDesigText){
                        message.error('No flight was selected for exporting');
                        return;
                    }
                    const legs = flight.legsArray || [];
                    const body = {
                        issuedBy: `${userAttr.given_name} ${userAttr.family_name}`,
                        orgName: flight.customerDoc?.name,
                        logoURL: logoURL,
                        desig: flight.desig,
                        departure: flight.departureDoc?.name,
                        aircraftNo: flight.aircraftID && flight.aircraftID.tailNum,
                        scheduledFlightDate: flight.scheduledFlightDate,
                        legs: legs.map((leg) => {
                            return {
                                departure: leg.departure,
                                destination: leg.destination,
                                flightTime: leg.flightTime || 0,
                                cargo: leg.cgoIDs.map((cgo)=>{
                                    return{
                                        _id: cgo._id,
                                        name: cgo.name,
                                        attentionTo: cgo.attentionTo,
                                        deliveredBy: cgo.deliveredBy,
                                        weight: cgo.weight,
                                        departure: cgo.departureDoc?.name,
                                        destination: cgo.destinationDoc?.name,
                                        chargeCode: cgo.chargeCode && cgo.chargeCode.toUpperCase(),
                                    }
                                }),
                                passengers: leg.paxIDs.map((pax) => {
                                    return {
                                        _id: pax._id,
                                        lastName: pax.lastName,
                                        firstName: pax.firstName,
                                        employer: pax.employerDoc?.name,
                                        chargeCode: pax.chargeCode && pax.chargeCode.toUpperCase(),
                                        paxWeight: pax.paxWeight,
                                        bagWeight: pax.bagWeight,
                                        departure: pax.departureDoc?.name,
                                        destination: pax.destinationDoc?.name,
                                        dob: pax.personDoc?.dob
                                    }
                                })
                            }
                        })
                    }
                    fetchPrintout(body, true, `${flight.scheduledFlightDate} ${flight.desig} Flight Manifest.xlsx`)
                        .catch(() => {
                            message.error('Failed to download printout', 3);
                        })
                }
                return (
                    <Button
                        {...restProps}
                        size="small"
                        style={{ color: 'green' }}
                        onClick={handleClick}
                        loading={fetching}
                        icon="file-excel"
                        title={"Download manifest printout for flight " + flight.desig}
                    >Download</Button>
                )
            }}
        </Query>
    )
}

PrintoutButton = WithOrgData( withCognitoUser( PrintoutButton ) )

export default ManifestDetail;